import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAwardList,
  getAwardById,
  createAward,
  updateAward,
  patchAward,
  deleteAward,
} from "../api/awards.api";
import type { AwardPayload, GetAwardListParams } from "@/interface/award";
import { awardsKeys, candidateProfileKeys, STALE_5_MIN } from "@/lib/queryKey";

export const useAwardList = (params?: GetAwardListParams) =>
  useQuery({
    queryKey: awardsKeys.list(params),
    queryFn: () => getAwardList(params),
    ...STALE_5_MIN,
  });

export const useAwardDetail = (id: number | string, enabled = true) =>
  useQuery({
    queryKey: awardsKeys.detail(id),
    queryFn: () => getAwardById(id),
    enabled: Boolean(id) && enabled,
    ...STALE_5_MIN,
  });

export const useCreateAward = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAward,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: awardsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.awards() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};

export const useUpdateAward = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number | string;
      payload: AwardPayload;
    }) => updateAward(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: awardsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: awardsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.awards() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};

export const usePatchAward = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<AwardPayload>;
    }) => patchAward(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: awardsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: awardsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.awards() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};

export const useDeleteAward = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => deleteAward(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: awardsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: candidateProfileKeys.awards() });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};
